import React, { Component } from 'react';
import PropTypes from 'prop-types';
import PageClasses from 'Webapp/shared/app/components/page-classes';
import Header from 'Webapp/shared/app/components/base/header';
import Subscriptions from 'Webapp/shared/app/components/subscriptions';
import Input from 'Webapp/shared/app/components/base/input';
import Button from 'Webapp/shared/app/components/button';
import LoadingSpinner from 'ComponentLibrary/icons/loading-spinner';
import SignupDisclaimer from 'Webapp/shared/app/components/signup-disclaimer';

import styled from '@emotion/styled';
import { SPACING } from 'Style/spacing';
import { BODY_TYPES } from 'Style/typography';
import { BREAKPOINTS } from 'Style/breakpoints';

import withT from 'ComponentLibrary/hocs/withT';
import { AppTheme, FrictionlessFlowType } from 'Webapp/enums';
import withFrictionlessSignup from 'Webapp/shared/app/hocs/withFrictionlessSignup';
import withReCaptcha from 'Webapp/shared/app/hocs/withReCaptcha';

import connector from 'Utils/connector';
import connectResponsive from 'Webapp/shared/app/connectors/connectResponsive';
import connectEmailSettings from 'Webapp/shared/app/connectors/connectEmailSettings';
import connectTheme from 'Webapp/shared/app/connectors/connectTheme';

import AnchoredDialog, {
  ANCHORED_DIALOG_POSITION_X,
  ANCHORED_DIALOG_POSITION_Y,
} from 'ComponentLibrary/utility/anchored-dialog';

const Wrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
});

const HeaderWrapper = styled.div(
  {
    textAlign: 'center',
    margin: `${SPACING.BASE8X} 0`,
  },
  BREAKPOINTS.phone({
    margin: `${SPACING.LARGE} 0`,
    textAlign: 'left',
  }),
);

const Title = styled(Header)(BODY_TYPES.BRAND_HEADING_MEDIUM, {
  marginBottom: SPACING.BASE,
});

const Subtitle = styled.div(BODY_TYPES.LARGE_STANDARD);

const Content = styled.div(
  {
    display: 'grid',
    columnGap: `${SPACING.BASE8X}`,
    gridTemplateColumns: 'minmax(200px, 500px) minmax(200px, 500px)',
  },
  BREAKPOINTS.phone({
    display: 'block',
  }),
);

const FormWrapper = styled.div(
  {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  BREAKPOINTS.phone({
    marginTop: `${SPACING.BASE4X}`,
  }),
);

const Form = styled.form({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  rowGap: `${SPACING.LARGE}`,
  marginBottom: `${SPACING.BASE}`,
});

const EmailInput = styled(Input)({
  width: '100%',
});

const SubmitButton = styled(Button)({
  width: 'fit-content',
});

const errorId = 'submit-errors';

class Newsletters extends Component {
  constructor(props) {
    super(props);

    this.state = {
      dirty: false,
      hasCheckedSubscriptions: false,
    };

    this.onChange = this.onChange.bind(this);
    this.handleNewsletterSubscribe = this.handleNewsletterSubscribe.bind(this);
    this.handleSubmitNewsletter = this.handleSubmitNewsletter.bind(this);
    this.setHasCheckedSubscriptions =
      this.setHasCheckedSubscriptions.bind(this);
    this.handleMouseEnterSubscribeButton =
      this.handleMouseEnterSubscribeButton.bind(this);
    this.handleMouseLeaveSubscribeButton =
      this.handleMouseLeaveSubscribeButton.bind(this);
    this.handleFocusSubscribeButton =
      this.handleFocusSubscribeButton.bind(this);
    this.handleBlurSubscribeButton = this.handleBlurSubscribeButton.bind(this);

    this.subscribeButtomElement = React.createRef();
  }

  componentDidMount() {
    const {
      emailGroups,
      emailGroupsLoaded,
      usageTrackNewsletterEnter,
      onPendingSubscriptionGroupsChange,
      getEmailSettingsMapping,
    } = this.props;

    usageTrackNewsletterEnter();

    if (emailGroupsLoaded) {
      onPendingSubscriptionGroupsChange(emailGroups);
    } else {
      // Email config (list of available subscriptions)
      // Used to populate inline frictionless newsletter signup
      // components on specific pages
      getEmailSettingsMapping();
    }
  }

  componentDidUpdate(prevProps) {
    const { reCaptchaFailedValidation } = this.props;
    if (!prevProps.emailGroupsLoaded && this.props.emailGroupsLoaded) {
      this.props.onPendingSubscriptionGroupsChange(this.props.emailGroups);
    }

    if (!prevProps.reCaptchaFailedValidation && reCaptchaFailedValidation) {
      this.props.usageTrackNewsletterSubmit(false);
    }
  }

  onChange(updatedPendingSubscriptionGroups) {
    this.setState({
      dirty: true,
    });

    this.props.onPendingSubscriptionGroupsChange(
      updatedPendingSubscriptionGroups,
    );
  }

  setHasCheckedSubscriptions(hasCheckedSubscriptions) {
    this.setState({
      hasCheckedSubscriptions,
    });
  }

  handleMouseEnterSubscribeButton() {
    this.setState({ isTooltipOpen: true });
  }

  handleMouseLeaveSubscribeButton() {
    this.setState({ isTooltipOpen: false });
  }

  handleFocusSubscribeButton() {
    this.setState({ isTooltipOpen: true });
  }

  handleBlurSubscribeButton() {
    this.setState({ isTooltipOpen: false });
  }

  async handleNewsletterSubscribe(e) {
    await this.props.onSubmit(e, FrictionlessFlowType.NEWSLETTERS);
    this.props.usageTrackNewsletterSubmit(true);
  }

  async handleSubmitNewsletter(e) {
    e.preventDefault();
    const {
      validateReCaptcha,
      usageTrackNewsletterSubmitClick,
      pendingSubscriptionGroups,
    } = this.props;
    usageTrackNewsletterSubmitClick(pendingSubscriptionGroups);
    await validateReCaptcha('newsletters', this.handleNewsletterSubscribe);
  }

  render() {
    const {
      t,
      isPhone,
      email,
      onboardPending,
      onEmailChange,
      emailGroupsLoaded,
      pendingSubscriptionGroups,
      isSubmittable,
      ReCaptchaUI,
      isReCaptchaPending,
      frictionlessEnter,
    } = this.props;

    const { dirty, hasCheckedSubscriptions, isTooltipOpen } = this.state;

    const submitDisabled =
      !isSubmittable ||
      isReCaptchaPending ||
      !dirty ||
      !hasCheckedSubscriptions;
    const ariaInvalid = isTooltipOpen && submitDisabled;
    const ariaErrormessage = submitDisabled && isTooltipOpen ? errorId : '';

    const formWrapper = (
      <FormWrapper>
        <Form onSubmit={this.handleSubmitNewsletter}>
          <EmailInput
            type="email"
            name="email"
            placeholder={t('newsletters_email_placeholder')}
            value={email}
            onChange={onEmailChange}
            onKeyUp={() => frictionlessEnter(FrictionlessFlowType.NEWSLETTERS)}
            disabled={onboardPending}
            autoFocus
          />
          {onboardPending ? (
            <LoadingSpinner />
          ) : (
            <>
              {isTooltipOpen && !isSubmittable && (
                <AnchoredDialog
                  id={errorId}
                  targetRef={this.subscribeButtomElement}
                  styleVariation={AnchoredDialog.styleVariations.ERROR}
                  positionX={ANCHORED_DIALOG_POSITION_X.LEFT}
                  positionY={ANCHORED_DIALOG_POSITION_Y.BELOW}
                >
                  {t('needs_email_and_one_newsletter')}
                </AnchoredDialog>
              )}
              <div
                ref={this.subscribeButtomElement}
                onMouseEnter={this.handleMouseEnterSubscribeButton}
                onMouseLeave={this.handleMouseLeaveSubscribeButton}
                onFocus={this.handleFocusSubscribeButton}
                onBlur={this.handleBlurSubscribeButton}
              >
                <SubmitButton
                  name="newsletters__form-submit"
                  className="button--primary button--block"
                  type="submit"
                  disabled={submitDisabled}
                  ariaDisabled={submitDisabled}
                  ariaInvalid={ariaInvalid}
                  ariaDescribedby={ariaErrormessage}
                >
                  {t('newsletters_submit_button')}
                </SubmitButton>
              </div>
            </>
          )}
        </Form>
        {ReCaptchaUI}
        <SignupDisclaimer isNewsletter />
      </FormWrapper>
    );
    const subscriptionsWrapper = (
      <div>
        {(emailGroupsLoaded && (
          <Subscriptions
            pendingSubscriptionGroups={pendingSubscriptionGroups}
            onChange={this.onChange}
            setHasCheckedSubscriptions={this.setHasCheckedSubscriptions}
            omitGroups
            newslettersOnly
            disabled={onboardPending}
          />
        )) ||
          null}
      </div>
    );

    return (
      <PageClasses>
        <Wrapper>
          <HeaderWrapper>
            <Title primary>{t('newsletters_title')}</Title>
            <Subtitle>{t('newsletters_subtitle')}</Subtitle>
          </HeaderWrapper>
          <Content>
            {isPhone ? subscriptionsWrapper : formWrapper}
            {isPhone ? formWrapper : subscriptionsWrapper}
          </Content>
        </Wrapper>
      </PageClasses>
    );
  }
}

Newsletters.propTypes = {
  emailGroups: PropTypes.array,
  emailGroupsLoaded: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  isPhone: PropTypes.bool.isRequired,
  usageTrackNewsletterEnter: PropTypes.func.isRequired,
  usageTrackNewsletterSubmit: PropTypes.func.isRequired,
  usageTrackNewsletterSubmitClick: PropTypes.func.isRequired,
  onEmailChange: PropTypes.func.isRequired,
  onPendingSubscriptionGroupsChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmittable: PropTypes.bool.isRequired,
  pendingSubscriptionGroups: PropTypes.array,
  email: PropTypes.string.isRequired,
  onboardPending: PropTypes.bool.isRequired,
  appTheme: PropTypes.oneOf(Object.values(AppTheme)),
  ReCaptchaUI: PropTypes.node,
  validateReCaptcha: PropTypes.func.isRequired,
  isReCaptchaPending: PropTypes.bool.isRequired,
  reCaptchaFailedValidation: PropTypes.bool.isRequired,
  getEmailSettingsMapping: PropTypes.func.isRequired,
  frictionlessEnter: PropTypes.func.isRequired,
};

Newsletters.defaultProps = {
  emailGroups: [],
  pendingSubscriptionGroups: null,
};

export default connector(
  connectEmailSettings,
  connectResponsive,
  connectTheme,
)(withReCaptcha(withFrictionlessSignup(withT(Newsletters))));
